import { useState, useCallback, useEffect, useMemo } from "react";
import IconContainer, { IconColor } from "@/components/ui/icon-container";
import Badge from "@/components/ui/badge";
import { FieldDisplayType } from "./types";
import { FIELD_TYPE_ICONS } from "./const";
import { File, Settings, AISparkle, LinkObject } from "@dreamhub/designsystem/icons";
import {
  useEntityFields,
  useUpdateEntityFieldsOrder,
  useUpdateEntityField,
  useDeleteEntityField
} from "@/pages/settings/hooks";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import {
  APIFieldItem,
  FieldDataSource,
  SettingsFieldType
} from "@/pages/settings/types";
import { TFunction } from "i18next";
import {
  getAnchoredFieldKeys,
  getFieldsAnchoredTo
} from "./anchoredFieldsConfig";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { isIcpRegistryField } from "@/entities/icpFit/helpers";

const RAW_FIELD_DISPLAY_TYPE_MAPPING: Record<string, FieldDisplayType> = {
  url: FieldDisplayType.URL,
  "single date": FieldDisplayType.DATE,
  "single select dropdown": FieldDisplayType.SINGLE_SELECT,
  "multi select dropdown": FieldDisplayType.MULTI_SELECT,
  currency: FieldDisplayType.CURRENCY,
  text: FieldDisplayType.TEXT,
  textarea: FieldDisplayType.TEXTAREA,
  number: FieldDisplayType.NUMBER,
  percentage: FieldDisplayType.PERCENTAGE,
  date: FieldDisplayType.DATE,
  date_range: FieldDisplayType.DATE_RANGE,
  single_select: FieldDisplayType.SINGLE_SELECT,
  multi_select: FieldDisplayType.MULTI_SELECT,
  email: FieldDisplayType.EMAIL,
  switch: FieldDisplayType.SWITCH,
  checkbox: FieldDisplayType.CHECKBOX,
  users: FieldDisplayType.USERS,
  companies: FieldDisplayType.COMPANIES,
  persons: FieldDisplayType.PEOPLE,
  leads: FieldDisplayType.LEADS,
  deals: FieldDisplayType.DEALS,
  partners: FieldDisplayType.PARTNERS,
  partner_contacts: FieldDisplayType.PARTNER_CONTACTS,
  partners_contacts: FieldDisplayType.PARTNER_CONTACTS,
  bool: FieldDisplayType.SWITCH,
  file_upload: FieldDisplayType.FILE_UPLOAD
};

export const normalizeFieldDisplayType = (
  fieldType: string | FieldDisplayType | null | undefined
): FieldDisplayType | undefined => {
  if (!fieldType) return undefined;
  if (fieldType in FIELD_TYPE_ICONS) return fieldType as FieldDisplayType;
  return RAW_FIELD_DISPLAY_TYPE_MAPPING[fieldType.toLowerCase()];
};

export const getFieldDisplayTypeIcon = (
  fieldType: string | FieldDisplayType | null | undefined,
  color?: IconColor
) => {
  const normalizedFieldDisplayType = normalizeFieldDisplayType(fieldType);
  const IconComponent = normalizedFieldDisplayType
    ? FIELD_TYPE_ICONS[normalizedFieldDisplayType]
    : File;

  return (
    <IconContainer size="sm" color={color ? color : "primary"}>
      <IconComponent />
    </IconContainer>
  );
};

export const getFieldDisplayTypeLabel = (
  fieldType: string | FieldDisplayType,
  t: TFunction<"translation", undefined>
): string => {
  const normalizedFieldDisplayType =
    normalizeFieldDisplayType(fieldType) ?? fieldType;
  return t(
    `settings.deals.fieldManagement.fieldDisplayTypes.${normalizedFieldDisplayType}`,
    {
      defaultValue: normalizedFieldDisplayType
    }
  );
};

interface DataSourceConfig {
  text?: string;
  icon?: any;
  multiple?: Array<{ text: string; icon?: any }>;
}

const getDataSourceConfig = (
  t: (key: string) => string
): Record<FieldDataSource, DataSourceConfig> => ({
  [FieldDataSource.SYSTEM]: {
    text: t("settings.deals.fieldManagement.dataSources.system"),
    icon: Settings
  },
  [FieldDataSource.MANUAL]: {
    text: t("settings.deals.fieldManagement.dataSources.manual")
  },
  [FieldDataSource.HYBRID]: {
    multiple: [
      { text: t("settings.deals.fieldManagement.dataSources.manual") },
      {
        text: t("settings.deals.fieldManagement.dataSources.dreamer"),
        icon: AISparkle
      }
    ]
  },
  [FieldDataSource.DREAMER]: {
    text: t("settings.deals.fieldManagement.dataSources.dreamer"),
    icon: AISparkle
  },
  [FieldDataSource.COMPANIES]: {
    text: t("settings.deals.fieldManagement.dataSources.company"),
    icon: LinkObject
  },
  [FieldDataSource.LEADS]: {
    text: t("settings.deals.fieldManagement.dataSources.lead"),
    icon: LinkObject
  },
  [FieldDataSource.PEOPLE]: {
    text: t("settings.deals.fieldManagement.dataSources.person"),
    icon: LinkObject
  },
  [FieldDataSource.DEALS]: {
    text: t("settings.deals.fieldManagement.dataSources.deals"),
    icon: LinkObject
  },
  [FieldDataSource.DEAL]: {
    text: t("settings.deals.fieldManagement.dataSources.deal"),
    icon: LinkObject
  }
});

const createStyledBadge = (
  text: string,
  IconComponent?: any,
  key?: number
): JSX.Element => (
  <Badge
    key={key}
    text={text}
    icon={
      IconComponent ? (
        <IconContainer size="sm" color="primary">
          <IconComponent />
        </IconContainer>
      ) : undefined
    }
    textVariant="smBody"
    className="h-6 px-[6px] py-1 rounded-[6px] border-[0.5px] border-border-badge-selected"
  />
);

export const getDataSourceBadge = (
  dataSource: string | FieldDataSource | null | undefined,
  t: (key: string) => string
): JSX.Element | JSX.Element[] => {
  if (!dataSource) return createStyledBadge("Unknown");

  let normalizedDataSource: FieldDataSource;

  if (Object.values(FieldDataSource).includes(dataSource as FieldDataSource)) {
    normalizedDataSource = dataSource as FieldDataSource;
  } else {
    const dataSourceMapping: Record<string, FieldDataSource> = {
      system: FieldDataSource.SYSTEM,
      manual: FieldDataSource.MANUAL,
      hybrid: FieldDataSource.HYBRID,
      company: FieldDataSource.COMPANIES,
      lead: FieldDataSource.LEADS,
      person: FieldDataSource.PEOPLE,
      deal: FieldDataSource.DEALS
    };
    normalizedDataSource =
      dataSourceMapping[dataSource.toLowerCase()] ||
      (dataSource as FieldDataSource);
  }

  const config = getDataSourceConfig(t)[normalizedDataSource];

  if (!config) {
    const capitalizedText = dataSource
      ? dataSource.charAt(0).toUpperCase() + dataSource.slice(1).toLowerCase()
      : "Unknown";
    return createStyledBadge(capitalizedText);
  }

  if (config.multiple) {
    return (
      <div className="flex gap-1">
        {config.multiple.map((badgeConfig, index) =>
          createStyledBadge(badgeConfig.text, badgeConfig.icon, index)
        )}
      </div>
    );
  }

  return createStyledBadge(config.text || "", config.icon);
};

// With the hide-field flag on, hidden fields (showInDetailedView=false) stay
// in the table greyed-out so admins can toggle them back; otherwise only
// visible fields are listed (legacy behaviour). Header fields are structural
// and ICP fields are KPIs, so neither is ever manageable here.
export const getManageableFields = (
  fields: APIFieldItem[],
  hideFieldEnabled: boolean
): APIFieldItem[] =>
  (hideFieldEnabled
    ? fields
    : fields.filter((field) => field.showInDetailedView)
  ).filter(
    (field) =>
      field.fieldType !== SettingsFieldType.HEADER && !isIcpRegistryField(field)
  );

export const useFieldManagement = (
  entityType: EntityType = EntityType.DEAL
) => {
  const [allFields, setAllFields] = useState<APIFieldItem[]>([]);
  const { data: apiResponse, isLoading } = useEntityFields(entityType);
  const updateFieldsOrderMutation = useUpdateEntityFieldsOrder(entityType);
  const updateFieldMutation = useUpdateEntityField(entityType);
  const deleteFieldMutation = useDeleteEntityField(entityType);
  const { enabled: hideFieldEnabled } = useFeatureFlag(
    FeatureFlags.CRM_5207_HIDE_FIELD
  );

  const anchoredFieldKeys = useMemo(
    () => getAnchoredFieldKeys(entityType),
    [entityType]
  );

  useEffect(() => {
    if (apiResponse?.fields && apiResponse.fields.length > 0) {
      setAllFields(apiResponse.fields);
    }
  }, [apiResponse]);

  const filteredFields = getManageableFields(allFields, hideFieldEnabled);

  // The partner-people field is a header field on the partner object but does
  // not render in the partner page header, so it is not manageable here.
  const headerFields = allFields.filter(
    (field) =>
      field.fieldType === SettingsFieldType.HEADER &&
      field.displayType !== FieldDisplayType.PARTNER_CONTACTS
  );

  const handleToggleFieldVisibility = useCallback(
    async (fieldId: string) => {
      const target = allFields.find((field) => field.id === fieldId);
      if (!target) return;
      const nextVisible = !target.showInDetailedView;
      const previousVisible = target.showInDetailedView;

      setAllFields((prevFields) =>
        prevFields.map((field) =>
          field.id === fieldId
            ? { ...field, showInDetailedView: nextVisible }
            : field
        )
      );

      try {
        await updateFieldMutation.mutateAsync({
          data: { id: fieldId, showInDetailedView: nextVisible },
          pathParams: {
            entityType: entityTypeToEntityName[entityType],
            fieldId
          }
        });
      } catch (error) {
        // Revert only this field so a failure here can't clobber unrelated
        // changes (reorders or other toggles) made while the request was open.
        setAllFields((prevFields) =>
          prevFields.map((field) =>
            field.id === fieldId
              ? { ...field, showInDetailedView: previousVisible }
              : field
          )
        );
        console.error("Failed to toggle field visibility:", error);
      }
    },
    [updateFieldMutation, entityType, allFields]
  );

  const handleDeleteField = useCallback(
    async (fieldId: string) => {
      const previousFields = [...allFields];
      setAllFields((prevFields) =>
        prevFields.filter((field) => field.id !== fieldId)
      );

      try {
        await deleteFieldMutation.mutateAsync({
          pathParams: {
            entityType: entityTypeToEntityName[entityType],
            fieldId
          }
        });
      } catch (error) {
        setAllFields(previousFields);
        console.error("Failed to delete field:", error);
      }
    },
    [deleteFieldMutation, entityType, allFields]
  );

  const handleDragEnd = useCallback(
    (result: any) => {
      const { destination, source } = result;
      if (!destination || destination.index === source.index) return;

      // Index mapping must match the rendered list (see filteredFields).
      const currentFilteredFields = getManageableFields(
        allFields,
        hideFieldEnabled
      );
      const draggedField = currentFilteredFields[source.index];

      // Prevent dragging anchored fields
      if (anchoredFieldKeys.includes(draggedField.fieldKey)) return;

      setAllFields((prevAllFields) => {
        const newAllFields = [...prevAllFields];
        const targetField = currentFilteredFields[destination.index];
        const anchoredToThisField = getFieldsAnchoredTo(
          entityType,
          draggedField.fieldKey
        );

        if (anchoredToThisField.length > 0) {
          // Move anchor field with its anchored fields
          const anchoredFields = currentFilteredFields.filter((f) =>
            anchoredToThisField.includes(f.fieldKey)
          );
          const fieldIdsToMove = [draggedField, ...anchoredFields].map(
            (f) => f.id
          );
          const remainingFields = newAllFields.filter(
            (f) => !fieldIdsToMove.includes(f.id)
          );

          let insertIndex = fieldIdsToMove.includes(targetField.id)
            ? Math.min(
                newAllFields.findIndex((f) => f.id === targetField.id),
                remainingFields.length
              )
            : remainingFields.findIndex((f) => f.id === targetField.id) +
              (destination.index > source.index ? 1 : 0);

          const orderedFieldsToInsert = [
            draggedField,
            ...anchoredFields.sort(
              (a, b) =>
                currentFilteredFields.findIndex((f) => f.id === a.id) -
                currentFilteredFields.findIndex((f) => f.id === b.id)
            )
          ];

          remainingFields.splice(insertIndex, 0, ...orderedFieldsToInsert);
          return updateFieldsOrder(remainingFields, updateFieldsOrderMutation);
        }

        // Regular field movement
        const draggedIndex = newAllFields.findIndex(
          (f) => f.id === draggedField.id
        );
        const targetIndex = newAllFields.findIndex(
          (f) => f.id === targetField.id
        );
        const [removed] = newAllFields.splice(draggedIndex, 1);
        newAllFields.splice(targetIndex, 0, removed);

        return updateFieldsOrder(newAllFields, updateFieldsOrderMutation);
      });
    },
    [
      updateFieldsOrderMutation,
      allFields,
      anchoredFieldKeys,
      entityType,
      hideFieldEnabled
    ]
  );

  const scoFilteredFieldsWithDreamer = allFields.filter(
    (field) =>
      field.fieldType === SettingsFieldType.SCO && field.filledByDreamer
  );

  return {
    fields: filteredFields,
    headerFields,
    scoFields: scoFilteredFieldsWithDreamer,
    isLoading,
    handleDeleteField,
    handleToggleFieldVisibility,
    handleDragEnd,
    anchoredFieldKeys,
    hideFieldEnabled
  };
};

const updateFieldsOrder = (
  fields: APIFieldItem[],
  mutation: ReturnType<typeof useUpdateEntityFieldsOrder>
) => {
  const updatedFields = fields.map((field, index) => ({
    ...field,
    order: index + 1
  }));
  mutation.mutate({ data: updatedFields.map((f) => f.id) });
  return updatedFields;
};
